import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getPublicStayGuest,
  giveGuestConsent,
  PublicStayGuest,
  recordGuestId,
  scanGuestId,
  StayPublicError,
  startGuestIdentity,
} from '../../../api/stays-public-client';
import { InhouseIdPanel } from '../../pages/stays/guest/inhouse-id-panel';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { AutoHeight, StateSwitch } from '../../../motion';
import { Heading, Subheading } from '../onboarding.styled';
import { SubTitle } from './public-stay-page.styled';
import {
  ConsentStep,
  IdentityStep,
  leaveFor,
  LoadingFill,
  OnError,
  PageStateView,
  POLL_FOR_MS,
  POLL_MS,
  saveReturn,
  StayShell,
  useLimitedRetry,
  useNoReferrer,
} from './stay-steps';
import { errorWords, t } from './stay-strings';

/**
 * ONE ADULT'S OWN CHECK-IN — /sg/:token (Lobbero Stays, plan S3, every_adult).
 *
 * The lead guest sent this adult their own link. The page greets them by first
 * name and asks for exactly two things: the consent, then the ID check. It
 * never shows the party, the lead's name, or anything of the stay beyond the
 * listing and the building.
 */

export type GuestView =
  | 'loading'
  | 'invalid'
  | 'limited'
  | 'error'
  | 'suspended'
  | 'consent'
  | 'identity'
  | 'verified';

export const chooseGuestView = (g: PublicStayGuest): GuestView => {
  if (g.suspended) return 'suspended';
  if (g.steps.identity === 'verified') return 'verified';
  if (g.steps.consent_given) return 'identity';

  return 'consent';
};

interface Props {
  /** Given by /s/return, which never has the token in its URL. */
  token?: string;
  /** Back from the ID partner: ask again until the answer lands. */
  returning?: boolean;
}

export const StayGuestPage: React.FC<Props> = ({ token: tokenProp, returning = false }) => {
  const params = useParams<{ token: string }>();
  const token = tokenProp ?? params.token ?? '';
  useNoReferrer();
  useDocumentTitle(t('en', 'documentTitle'));

  const [view, setView] = useState<GuestView>('loading');
  const [guest, setGuest] = useState<PublicStayGuest | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checking, setChecking] = useState(returning);
  const { canRetry, hold } = useLimitedRetry();
  const selfie = useRef(false);
  const seq = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStart = useRef(Date.now());

  useEffect(
    () => () => {
      seq.current += 1;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    },
    []
  );

  const load = useCallback(
    (quiet = false) => {
      const mine = ++seq.current;
      if (pollTimer.current) clearTimeout(pollTimer.current);
      pollTimer.current = null;
      if (!quiet) setView('loading');
      getPublicStayGuest(token)
        .then(data => {
          if (mine !== seq.current) return;
          if (!data || !data.guest_first_name) {
            setView('invalid');

            return;
          }
          setGuest(data);
          const chosen = chooseGuestView(data);
          setView(chosen);
          const waiting = data.steps.identity === 'pending' || data.steps.identity === 'not_started';
          const again = returning && chosen === 'identity' && waiting && Date.now() - pollStart.current < POLL_FOR_MS;
          setChecking(again);
          if (again) pollTimer.current = setTimeout(() => load(true), POLL_MS);
        })
        .catch(err => {
          if (mine !== seq.current) return;
          const status = err instanceof StayPublicError ? err.status : 0;
          if (status === 404) {
            setChecking(false);
            setView('invalid');

            return;
          }
          if (quiet && returning && Date.now() - pollStart.current < POLL_FOR_MS) {
            pollTimer.current = setTimeout(() => load(true), POLL_MS);

            return;
          }
          setChecking(false);
          if (status === 429) {
            hold();
            setView('limited');

            return;
          }
          setView('error');
        });
    },
    [token, returning, hold]
  );

  useEffect(() => {
    load();
  }, [load]);

  const onError: OnError = err => {
    if (!(err instanceof StayPublicError)) return errorWords('STAY_GUEST_ERROR');
    if (err.status === 404) {
      setView('invalid');

      return null;
    }
    switch (err.code) {
      case 'CONSENT_REQUIRED':
      case 'CONSENT_OUTDATED':
        getPublicStayGuest(token)
          .then(setGuest)
          .catch(() => undefined);
        setNotice(errorWords(err.code));
        setView('consent');

        return null;
      case 'IDENTITY_ATTEMPTS_EXHAUSTED':
        setGuest(g => (g ? { ...g, identity_attempts_left: 0 } : g));

        return null;
      case 'ALREADY_VERIFIED':
        load(true);

        return null;
      default:
        return errorWords(err.code);
    }
  };

  const agree = async (withSelfie: boolean) => {
    if (!guest) return;
    await giveGuestConsent(token, guest.consent.version, withSelfie);
    selfie.current = withSelfie;
    setGuest({ ...guest, steps: { ...guest.steps, consent_given: true } });
    setNotice(null);
    setView('identity');
  };

  const start = async () => {
    const out = await startGuestIdentity(token, selfie.current);
    // BEFORE the hop: /s/return carries no token, so it is kept here.
    saveReturn('sg', token);
    leaveFor(out.redirect_url);
  };

  // IS0: the in-house check runs on this page; 'vendor' is the S2 test fake's hosted page.
  const inhouse = !!guest && guest.id_flow !== 'vendor';
  const inhousePanel = guest ? (
    <InhouseIdPanel
      idPrefix="own"
      first={guest.guest_first_name}
      consent={{ text: guest.consent.identity_text, version: guest.consent.version, given: guest.steps.consent_given }}
      scanMode={guest.id_scan_mode || 'manual_only'}
      checkInDay={guest.stay.check_in}
      giveConsent={async () => {
        await giveGuestConsent(token, guest.consent.version, false);
        setNotice(null);
      }}
      scan={photo => scanGuestId(token, photo)}
      record={async body => {
        await recordGuestId(token, body);
        load(true);
      }}
      onError={onError}
    />
  ) : null;

  const greeting = guest ? (
    <>
      <Heading>{t('en', 'guestTitle', { first: guest.guest_first_name })}</Heading>
      <Subheading>
        {t('en', 'guestSub', { listing: guest.listing_name, building: guest.building.name })}
      </Subheading>
    </>
  ) : null;

  const renderView = () => {
    if (view === 'loading') return <LoadingFill />;
    if (view === 'invalid' || view === 'limited' || view === 'error' || view === 'suspended') {
      return <PageStateView state={view} canRetry={canRetry} onRetry={() => load()} />;
    }
    if (!guest) return <LoadingFill />;

    if (inhouse && (view === 'consent' || view === 'identity')) {
      return (
        <>
          {greeting}
          {notice ? <SubTitle>{notice}</SubTitle> : null}
          {guest.steps.identity === 'failed' ? <SubTitle>{t('en', 'idDoneRescan')}</SubTitle> : null}
          {inhousePanel}
        </>
      );
    }

    switch (view) {
      case 'consent':
        return (
          <>
            {greeting}
            <ConsentStep consent={guest.consent} level={2} onAgree={agree} onError={onError} notice={notice} />
          </>
        );
      case 'identity':
        return (
          <>
            {greeting}
            <IdentityStep
              identity={guest.steps.identity}
              attemptsLeft={guest.identity_attempts_left}
              level={2}
              checking={checking}
              onStart={start}
              onError={onError}
            />
          </>
        );
      case 'verified':
        return (
          <>
            {greeting}
            <SubTitle>
              {!inhouse
                ? t('en', 'guestVerified')
                : guest.steps.id_review === 'needs_review'
                  ? t('en', 'idDoneHostCheck', { first: guest.guest_first_name })
                  : t('en', 'idDoneConfirmed', { first: guest.guest_first_name })}
            </SubTitle>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <StayShell>
      <AutoHeight>
        <StateSwitch state={view}>{renderView()}</StateSwitch>
      </AutoHeight>
    </StayShell>
  );
};
